import { createSlice, current, isAnyOf } from "@reduxjs/toolkit"
import type { PayloadAction } from '@reduxjs/toolkit'
import { InterviewFlow } from '../interface'
import {
  getInterviewFlow,
  createAndSaveInterviewFlow,
  updateInterviewFlow,
  getInterviewNodesByFlow,
  getExistingFlowsForTenant,
  getInterviewFlowGlobalTemplateListing,
  generateInterviewFlow,
  generateInterviewFlowByJob
} from "./InterviewFlowAction"
import { Branch, InterviewNode, QuestionType }
  from "../../../../CandidatePortal/scenes/Interview/Chatbot/interface"
import { update } from "final-form-arrays"

interface InterviewFlowState {
  loading: Boolean
  error: any | undefined
  changesMade: Boolean
  interviewFlow: InterviewFlow
  interviewNodeList: InterviewNode[]

  existingInterviewFlows: InterviewFlow[]
  interviewFlowGlobalTemplateListing: InterviewFlow[]
  interviewFlowGlobalTemplateTotalRecords: Number
}

const initialState = {
  loading: false,
  error: undefined,
  changesMade: false,
  interviewFlow: {} as InterviewFlow,
  interviewNodeList: [] as InterviewNode[],

  existingInterviewFlows: [] as InterviewFlow[],
  interviewFlowGlobalTemplateListing: [] as InterviewFlow[],
  interviewFlowGlobalTemplateTotalRecords: 0,
} as InterviewFlowState

const interviewFlowSlice = createSlice({
  name: 'interviewFlow',
  initialState,
  reducers: {
    toggleChangesMade: (state: InterviewFlowState) => {
      // return { ...state, changesMade: true };
      state.changesMade = true
    },
    updateInterviewNodeList: (state: any, action: PayloadAction<InterviewNode[]>) => {
      state.interviewNodeList = action.payload
      state.interviewFlow.interviewNodeList = action.payload
    },
    deleteInterviewNode: (state: any, action: PayloadAction<InterviewNode>) => {
      let tempNodeList: InterviewNode[] = structuredClone(current(state.interviewNodeList))
      let indexToRemove = (tempNodeList.findIndex
        ((tempNode: InterviewNode) => tempNode.name === action.payload.name))
      tempNodeList.splice(indexToRemove, 1)
      tempNodeList.forEach((currentNode: InterviewNode) => {
        switch (currentNode.typeOfQuestion) {
          case (QuestionType.ConfirmationQuestion):
            if (currentNode.confirmationSetting) {
              if (currentNode.confirmationSetting.flowToIfNo === action.payload.name)
                currentNode.confirmationSetting.flowToIfNo = ''
              if (currentNode.confirmationSetting.flowToIfYes === action.payload.name)
                currentNode.confirmationSetting.flowToIfYes = ''
            }
            break
          case (QuestionType.OpenEndedQuestion):
            if (currentNode.nextStep === action.payload.name)
              currentNode.nextStep = ''
            break
          case (QuestionType.DelegatedQuestion):
            if (currentNode.nextStep === action.payload.name)
              currentNode.nextStep = ''
            break
          case (QuestionType.ConditionalQuestion):
            if (currentNode.conditionalSetting) {
              if (currentNode.conditionalSetting.defaultStep === action.payload.name)
                currentNode.conditionalSetting.defaultStep = ''

              currentNode.conditionalSetting.branches.forEach((branch: Branch) => {
                if (branch.nextStep === action.payload.name)
                  branch.nextStep = ''
              })
            }
            break
        }
      })

      // const newInterviewFlow = { ...state.interviewFlow, interviewNodeList: tempNodeList }
      // return { ...state, interviewNodeList: tempNodeList, interviewFlow: newInterviewFlow, changesMade: true }
      state.changesMade = true
      state.interviewNodeList = tempNodeList
      state.interviewFlow.interviewNodeList = tempNodeList
    },
    //update also includes inserting new 
    updateNodeInNodeList: (state: any, action: PayloadAction<InterviewNode>) => {
      const newNode = action.payload
      let tempNodeList: InterviewNode[] = structuredClone(current(state.interviewNodeList))
      let updateIndex = state.interviewNodeList.findIndex((node: InterviewNode) => node.hashId === newNode.hashId)
      if (updateIndex >= 0) {
        let oldNodeName = tempNodeList[updateIndex].name
        let newNodeName: string = action.payload.name

        tempNodeList.forEach((currentNode: InterviewNode) => {
          switch (currentNode.typeOfQuestion) {
            case (QuestionType.ConfirmationQuestion):
              if (currentNode.confirmationSetting) {
                if (currentNode.confirmationSetting.flowToIfNo === oldNodeName)
                  currentNode.confirmationSetting.flowToIfNo = newNodeName
                if (currentNode.confirmationSetting.flowToIfYes === oldNodeName)
                  currentNode.confirmationSetting.flowToIfYes = newNodeName
              }
              break
            case (QuestionType.OpenEndedQuestion):
              if (currentNode.nextStep === oldNodeName)
                currentNode.nextStep = newNodeName
              break
            case (QuestionType.DelegatedQuestion):
              if (currentNode.nextStep === oldNodeName)
                currentNode.nextStep = newNodeName
              break
            case (QuestionType.ConditionalQuestion):
              if (currentNode.conditionalSetting) {
                if (currentNode.conditionalSetting.defaultStep === oldNodeName)
                  currentNode.conditionalSetting.defaultStep = newNodeName
  
                if (currentNode.conditionalSetting.branches)
                  currentNode.conditionalSetting.branches.forEach((branch: Branch) => {
                    if (branch.nextStep === oldNodeName)
                      branch.nextStep = newNodeName
                  })
              }
              break
          }
          tempNodeList[updateIndex] = Object.assign(tempNodeList[updateIndex], newNode)
        })
      }
      else {
        newNode.hashId = Math.random()
        tempNodeList = tempNodeList.concat(newNode)
      }

      state.changesMade = true
      state.interviewNodeList = tempNodeList
      state.interviewFlow.interviewNodeList = tempNodeList
    },
    setInterviewFlow: (state: any, action: PayloadAction<InterviewFlow>) => {
      state.interviewFlow = {...state.interviewFlow, ...action.payload}
    },
    setInterviewFlowEnum: (state: any, action: PayloadAction<any>) => {
      state.changesMade = true
      state.interviewFlow.interviewFlowEnum = action.payload
    },
    addNodesToNodeList: (state: InterviewFlowState, action: PayloadAction<any>) => {
      let nodeList: InterviewNode[] = JSON.parse(JSON.stringify(action.payload))
      /*
      let interviewNodes = action.payload
      nodeList = interviewNodes.map((eachIntent: InterviewNode) => {
        return eachIntent
      })
      */
      let nameList: string[] = nodeList.map((eachIntent: InterviewNode) => {
        return eachIntent.name
      })
      nodeList.forEach((currentNode: InterviewNode) => {
        switch (currentNode.typeOfQuestion) {
          case (QuestionType.ConfirmationQuestion):
            if (!nameList.includes(currentNode.confirmationSetting.flowToIfNo))
              currentNode.confirmationSetting.flowToIfNo = ''
            if (!nameList.includes(currentNode.confirmationSetting.flowToIfYes))
              currentNode.confirmationSetting.flowToIfYes = ''
            break
          case (QuestionType.OpenEndedQuestion):
            if (!nameList.includes(currentNode.nextStep))
              currentNode.nextStep = ''
            break
          case (QuestionType.DelegatedQuestion):
            if (!nameList.includes(currentNode.nextStep))
              currentNode.nextStep = ''
            break
          case (QuestionType.ConditionalQuestion):
            if (!nameList.includes(currentNode.conditionalSetting.defaultStep))
              currentNode.conditionalSetting.defaultStep = ''
            currentNode.conditionalSetting.branches.forEach((branch: Branch) => {
              if (!nameList.includes(branch.nextStep))
                branch.nextStep = ''
            })
            break
        }
      })
      let concatenatedNodeList = state.interviewNodeList.concat(nodeList);
      state.interviewNodeList = concatenatedNodeList
      state.changesMade = true
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInterviewFlow.pending, (state: any) => {
        state.loading = true
        state.error = null
      })
      .addCase(getInterviewFlow.fulfilled, (state: any, { payload }) => {
        state.loading = false

        const retrievedInterviewflow: InterviewFlow = payload
        const importedFlow = state.interviewFlow.id !== undefined &&
          state.interviewFlow.id !== retrievedInterviewflow.id;
        // console.log(importedFlow)

        if (importedFlow) console.log('imported flow', retrievedInterviewflow.id)

        if (retrievedInterviewflow.interviewNodeList) {
          retrievedInterviewflow.interviewNodeList.forEach((node: InterviewNode) => {
            node.hashId = Math.random() //Assign random temporary IDs for interviewflow frontend

            // if import, remove id!
            if (importedFlow) {
              node.id = undefined
            }
          })
        }

        // update node list
        state.interviewFlow = retrievedInterviewflow
        state.interviewNodeList = retrievedInterviewflow.interviewNodeList

        // check if it's import flow
        if (importedFlow) {
          // only set interview node list
          state.interviewFlow.interviewNodeList = retrievedInterviewflow.interviewNodeList
          state.changesMade = true
        }
      })
      .addCase(getInterviewFlow.rejected, (state: InterviewFlowState, { payload }) => {
        state.loading = false

        // reset interview flow and node
        state.interviewFlow = {} as InterviewFlow
        state.interviewNodeList = []
        
        // retrieve error message out of payload
        const errorMessage = (typeof payload === 'string') ? payload : undefined;
        if (errorMessage && errorMessage.indexOf('not found') === -1) {
          // if error is due to interview flow not found, dont throw error!
          state.error = payload
        }
      })
      .addCase(createAndSaveInterviewFlow.pending, (state: InterviewFlowState) => {
        state.loading = true
        state.error = null
      })
      .addCase(createAndSaveInterviewFlow.fulfilled, (state: InterviewFlowState, { payload }) => {
        state.interviewFlow = payload
        state.changesMade = false
        state.loading = false
      })
      .addCase(createAndSaveInterviewFlow.rejected, (state: InterviewFlowState, { payload }) => {
        state.loading = false
        state.error = payload
      })
      .addCase(updateInterviewFlow.pending, (state: InterviewFlowState) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateInterviewFlow.fulfilled, (state: InterviewFlowState, { payload }) => {
        state.interviewFlow = payload
        state.changesMade = false
        state.loading = false
      })
      .addCase(updateInterviewFlow.rejected, (state: InterviewFlowState, { payload }) => {
        state.loading = false
        state.error = payload
      })
      .addCase(getInterviewNodesByFlow.pending, (state: InterviewFlowState) => {
        state.loading = true
        state.error = null
      })
      .addCase(getInterviewNodesByFlow.fulfilled, (state: InterviewFlowState, { payload }) => {
        let updatedExistingFlows = JSON.parse(JSON.stringify(state.existingInterviewFlows))
        let index = -1

        if (payload && payload.length > 0)
          index = updatedExistingFlows.findIndex((eachFlow: InterviewFlow) => eachFlow.id === payload[0].interviewFlowId)

        if (index > -1)
          updatedExistingFlows[index].interviewNodeList = payload

        state.loading = false
        state.changesMade = false
        state.existingInterviewFlows = updatedExistingFlows
      })
      .addCase(getInterviewNodesByFlow.rejected, (state: InterviewFlowState, { payload }) => {
        state.loading = false
        state.error = payload
      })
      .addCase(getExistingFlowsForTenant.pending, (state: InterviewFlowState) => {
        state.loading = true
        state.error = null
      })
      .addCase(getExistingFlowsForTenant.fulfilled, (state: InterviewFlowState, { payload }) => {
        state.existingInterviewFlows = payload
        state.loading = false
      })
      .addCase(getExistingFlowsForTenant.rejected, (state: InterviewFlowState, { payload }) => {
        state.loading = false
        state.error = payload
      })
      .addCase(getInterviewFlowGlobalTemplateListing.pending, (state: InterviewFlowState) => {
        state.loading = true
        state.error = null
      })
      .addCase(getInterviewFlowGlobalTemplateListing.fulfilled, (state: InterviewFlowState, { payload }) => {
        state.interviewFlowGlobalTemplateListing = payload.content
        state.interviewFlowGlobalTemplateTotalRecords = payload.totalElements
        state.loading = false
      })
      .addCase(getInterviewFlowGlobalTemplateListing.rejected, (state: InterviewFlowState, { payload }) => {
        state.loading = false
        state.error = payload
      })
      .addCase(generateInterviewFlow.pending, (state: InterviewFlowState) => {
        state.loading = true
        state.error = null
      })
      .addCase(generateInterviewFlow.fulfilled, (state: InterviewFlowState) => {
        state.loading = false
      })
      .addCase(generateInterviewFlow.rejected, (state: InterviewFlowState, { payload }) => {
        state.loading = false
        state.error = payload
      })
      .addCase(generateInterviewFlowByJob.pending, (state: InterviewFlowState) => {
        state.loading = true
        state.error = null
      })
      .addCase(generateInterviewFlowByJob.fulfilled, (state: InterviewFlowState) => {
        state.loading = false
      })
      .addCase(generateInterviewFlowByJob.rejected, (state: InterviewFlowState, { payload }) => {
        state.loading = false
        state.error = payload
      })
  }
})

export const { toggleChangesMade,
  updateInterviewNodeList,
  deleteInterviewNode,
  updateNodeInNodeList,
  setInterviewFlow,
  setInterviewFlowEnum,
  addNodesToNodeList } = interviewFlowSlice.actions;

export default interviewFlowSlice.reducer